import { GradientedScroller, connected, withErrorBoundary, NoResults, Spinner } from 'shared'
import { absolutizePath } from 'shared/util'
import { TopLevelPage, ModelGrid } from 'ui'
import EventCard from 'ui/events/EventCard'
import ParkCard from 'ui/parks/ParkCard'
import { EventApi, AnnouncementApi, Park, ParkApi, Menu, MenuApi, User } from 'api'
import { addDays, format, startOfMonth, endOfMonth, isValid } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import Announcements from '../events/Announcements'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import adaptivePlugin from '@fullcalendar/adaptive'
import interactionPlugin from '@fullcalendar/interaction'

calendarGlobal = []
intervalId = null

CalendarView = ({match}) ->
  feedItemId = match.params.id
  selectedPlacesPageSize = 8
  [calendar, setCalendar] = useState([
    {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
      key: 'selection'
    }
  ])
  [events, setEvents] = useState([])
  [showReservations, setShowReservations] = useState(false)
  [selectedPlaces, setSelectedPlaces] = useState([])
  [selectedPlacesPage, setSelectedPlacesPage] = useState(1)
  [reservablePlaces, setReservablePlaces] = useState([])
  [selectedDate, setSelectedDate] = useState(new Date())
  [reservableDates, setReservableDates] = useState([])
  [allEventMenus, setAllEventMenus] = useState([])
  [selectedMenuId, setSelectedMenuId] = useState(null)
  [eventMenuIds, setEventMenuIds] = useState({})
  [menuIdsWithEvents, setMenuIdsWithEvents] = useState([])
  [loading, setLoading] = useState(true)
  selectedPlacesRequestRef = useRef(null)
  scrollToAmenitiesRef = useRef(false)
  eventsRequestRef = useRef(0)
  lastLoadedRangeRef = useRef(null)
  selectedMenuIdRef = useRef(null)
  announcements = AnnouncementApi.kick('index')

  parseDate = (dateTime) ->
    format(dateTime, "yyyy'-'MM'-'dd")

  dateParams = (calendar) ->
    {
      start_date: parseDate(calendar.startDate),
      end_date: parseDate(calendar.endDate)
    }

  rangeKey = (calendar) ->
    "#{parseDate(calendar.startDate)}:#{parseDate(calendar.endDate)}"

  applyFullCalendarAccessibilityFix = ->
    document.querySelectorAll('.fc-col-header-cell[role="columnheader"], .fc-col-header-cell[role="presentation"]').forEach (th) ->
      if not th.hasAttribute('scope')
        th.setAttribute('scope', 'col')

    document.querySelectorAll('th[role="presentation"]').forEach (th) ->
      if not th.hasAttribute('scope')
        th.setAttribute('scope', 'col')

    document.querySelectorAll('.fc-scrollgrid').forEach (table) ->
      cells = table.querySelectorAll('td, th')
      if cells.length <= 7
        table.removeAttribute('role')
        table.setAttribute('role', 'list')
        table.querySelectorAll('.fc-daygrid-day').forEach (cell) ->
          cell.setAttribute('role', 'listitem')
      else
        table.setAttribute('role', 'grid')

    document.querySelectorAll('.fc-daygrid-day-number').forEach (el) ->
      return if el.tagName.toLowerCase() is 'button'  # Skip if already converted

      dateLabel = el.getAttribute('aria-label') or el.textContent?.trim()
      button = document.createElement('button')
      button.className = el.className
      button.textContent = el.textContent?.trim() or ''
      button.setAttribute('type', 'button')
      button.setAttribute('tabindex', '0')
      button.setAttribute('aria-label', "Select date #{dateLabel or ''}")

      if el.id then button.id = el.id

      el.parentNode.replaceChild(button, el)

  hideCalendarLinks = ->
    document.querySelectorAll('.fc-daygrid-day-number').forEach (el) ->
      if el.tagName.toLowerCase() is 'a'
        el.style.display = 'none'

  useEffect(() ->
    setTimeout(applyFullCalendarAccessibilityFix, 1500)
    setTimeout(hideCalendarLinks, 50)
  , [])

  useEffect(() ->
    MenuApi.load().then (allMenus) ->
      eventMenus = (allMenus or []).filter (menu) -> menu.item_type == 'Event' and menu.valid
      setAllEventMenus(eventMenus)
    undefined
  , [])

  fetchReservableDates = (startDate = null, endDate = null) ->
    start = startDate or new Date()
    end = endDate or endOfMonth(new Date())
    today = new Date()
    today = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    return setReservableDates([]) if end < today
    start = today if start < today

    ParkApi.availableDates({
      start_date: format(start, "yyyy-MM-dd"),
      end_date: format(end, "yyyy-MM-dd")
    }).then (dates) ->
      setReservableDates(dates.map((date) -> { date }))

  handleCalendarEventClick = (info) ->
    return if loading
    info.jsEvent?.preventDefault()
    info.jsEvent?.stopPropagation()

    if info.event?.title?.includes("Open to Reserve")
      application.go('/community-calendar') if feedItemId
      scrollToAmenitiesRef.current = true
      loadPlacesForDate(new Date(info.event.start))
      return

    targetUid = info.event.extendedProps?.targetUid
    application.go("/community-calendar/#{targetUid}") if targetUid

  loadPlacesForDate = (date) ->
    normalizedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    formattedDate = format(normalizedDate, "yyyy-MM-dd")
    setSelectedDate(normalizedDate)
    setSelectedPlacesPage(1)

    # Ignore stale responses when user clicks dates quickly.
    requestKey = "#{formattedDate}-#{Date.now()}"
    selectedPlacesRequestRef.current = requestKey

    ParkApi.openAvailable({ start_date: formattedDate, end_date: formattedDate }).then (data) ->
      return unless selectedPlacesRequestRef.current == requestKey
      places = data or []
      setSelectedPlaces(places)
      return unless scrollToAmenitiesRef.current
      scrollToAmenitiesRef.current = false
      return unless places.length > 0
      setTimeout ->
        document.getElementById('available-amenity-reservations')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      , 50

  loadData = (calendar = null) ->
    calendar = {
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date())
    } unless calendar

    key = rangeKey(calendar)
    requestId = ++eventsRequestRef.current
    lastLoadedRangeRef.current = key
    setLoading(true)

    params = dateParams(calendar)
    EventApi.filtered(params).then (data) ->
      return unless requestId == eventsRequestRef.current
      activeMenuId = selectedMenuIdRef.current
      menuIds = data.menuIdsWithEvents or []
      setEvents(data.events or [])
      setEventMenuIds(data.eventMenuIds or {})
      setMenuIdsWithEvents(menuIds)
      if activeMenuId and not menuIds.map(String).includes(String(activeMenuId))
        setSelectedMenuId(null)
      setLoading(false)
    , ->
      return unless requestId == eventsRequestRef.current
      setEvents([])
      setEventMenuIds({})
      setMenuIdsWithEvents([])
      setLoading(false)

    fetchReservableDates(calendar.startDate, calendar.endDate)

  selectMenu = (menuId) ->
    return if loading
    selectedMenuIdRef.current = menuId
    setSelectedMenuId(menuId)

  resetFilters = ->
    return if loading
    selectedMenuIdRef.current = null
    setSelectedMenuId(null)
    setSelectedPlaces([])
    setSelectedPlacesPage(1)

  updateDateRange = (range) ->
    setSelectedDate(range.selection.startDate)
    setCalendar([range.selection])

  printCalendar = ->
    nav = document.querySelector('.MainNav')
    footer = document.querySelector('footer')

    nav?.style.setProperty('display', 'none', 'important')
    footer?.style.setProperty('display', 'none', 'important')

    restore = ->
      nav?.style.removeProperty('display')
      footer?.style.removeProperty('display')
      window.removeEventListener('afterprint', restore)

    window.addEventListener('afterprint', restore)

    window.print()

  useEffect ->
    handleKeyDown = (e) ->
      if (e.ctrlKey or e.metaKey) and e.key.toLowerCase() is 'p'
        e.preventDefault()
        printCalendar()
    window.addEventListener('keydown', handleKeyDown)
    -> window.removeEventListener('keydown', handleKeyDown)
  , []

  # Load the full displayed month, not FullCalendar's visible grid (which includes padding days).
  handleDatesSet = (info) ->
    monthStart = startOfMonth(info.view.currentStart)
    monthEnd = endOfMonth(info.view.currentStart)
    newRange =
      startDate: monthStart
      endDate: monthEnd
      key: 'selection'
    key = rangeKey(newRange)
    return if lastLoadedRangeRef.current == key
    setCalendar([newRange])
    setSelectedDate(monthStart)
    setSelectedPlaces([])
    setSelectedPlacesPage(1)
    loadData(newRange)

  activeMenuIdSet = menuIdsWithEvents.map(String)
  menus = allEventMenus.filter (menu) -> activeMenuIdSet.includes(String(menu.id))

  selectedDateString = if selectedDate instanceof Date then format(selectedDate, "yyyy-MM-dd") else null
  selectedPlacesTotalPages = Math.max(1, Math.ceil(selectedPlaces.length / selectedPlacesPageSize))
  selectedPlacesCurrentPage = Math.min(selectedPlacesPage, selectedPlacesTotalPages)
  selectedPlacesStart = (selectedPlacesCurrentPage - 1) * selectedPlacesPageSize
  paginatedSelectedPlaces = selectedPlaces.slice(selectedPlacesStart, selectedPlacesStart + selectedPlacesPageSize)

  filteredEvents = if selectedMenuId
    events.filter (model) ->
      menuIds = eventMenuIds[model.id] or eventMenuIds[String(model.id)] or []
      menuIds.some (id) -> String(id) == String(selectedMenuId)
  else
    events

  calendarEvents = filteredEvents.flatMap((model) ->
    base = "/app#/community-calendar"
    if model.category == "multiday"
      startDate = new Date("#{model.date}T09:00:00")  # Default start time at 9 AM
      endDate = new Date("#{model.end_date}T18:00:00")  # End time at 6 PM

      return {
        id: model.id
        targetUid:  model.targetUid
        title: model.name
        start: startDate.toISOString()
        end: endDate.toISOString()
        url: "#{absolutizePath(base)}/#{model.targetUid}" 
        allDay: true
      }

    else if model.is_recurring and model.occurrences?  # Handle recurring events
      return model.occurrences.map((occurrence) ->
        occurrenceDate = new Date("#{occurrence.date}T#{occurrence.time}:00")
        endTime = new Date("#{occurrence.date}T#{occurrence.close_time}:00")
        return null unless isValid(occurrenceDate) and isValid(endTime)

        return {
          id: occurrence.id
          targetUid:  model.targetUid
          title: model.name
          start: occurrenceDate.toISOString()
          end: endTime.toISOString()
          allDay: false
          url: "#{absolutizePath(base)}/#{model.targetUid}"
        }
      ).filter(Boolean)

    else  # Regular single-day events
      startDate = new Date("#{model.date}T#{model.open_time_local}:00")
      endDate = new Date("#{model.date}T#{model.close_time_local}:00")

      return {
        id: model.id
        targetUid:  model.targetUid
        title: model.name
        start: startDate.toISOString()
        end: endDate.toISOString()
        url: "#{absolutizePath(base)}/#{model.targetUid}"
        allDay: false
      }
  )

  calendarEvents.push(
    ...reservableDates.map((entry) ->
      isSelectedReserveDate = selectedDateString == entry.date and selectedPlaces.length > 0
      {
        id: "reserve-#{entry.date}"
        title: "● Open to Reserve"
        start: entry.date
        allDay: true
        classNames: if isSelectedReserveDate then ["selected-open-to-reserve-event"] else []
      }
    )
  )

  <TopLevelPage className="Calendar" modelId={feedItemId} base="/community-calendar" returnUrl="/community-calendar">
    <h1
      className="visually-hidden"
    >
      Community Calendar
    </h1>
    {menus.length > 0 &&
      <div className={"row calendar-filters justify-content-center mt-3" + (if loading then " is-loading" else "")}>
        <div className="col">
          <h6 className="font-weight-bold text-uppercase text-black menu-header-items">Calendar Filters</h6>
          <div className="calendar-filter-buttons">
            <button
              type="button"
              disabled={loading}
              className={"smart-button btn primary custom-font-size custom-font-family" + (if not selectedMenuId then " active" else "") + (if loading then " disabled" else "")}
              onClick={-> selectMenu(null)}
            >
              All
            </button>
            {menus.map((menu) ->
              <button
                key={menu.id}
                type="button"
                disabled={loading}
                className={"smart-button btn primary custom-font-size custom-font-family" + (if String(selectedMenuId) == String(menu.id) then " active" else "") + (if loading then " disabled" else "")}
                onClick={-> selectMenu(menu.id)}
              >
                {menu.name}
              </button>
            )}
          </div>
        </div>
      </div>
    }
    <div className={"row calendar-section justify-content-center mt-3" + (if loading then " is-loading" else "")}>
      <div className="col position-relative">
        {loading &&
          <div className="calendar-loading-overlay">
            <Spinner text="Loading data..." />
          </div>
        }
        <FullCalendar
          schedulerLicenseKey="0456860429-fcs-1763595698"
          plugins={[ dayGridPlugin, timeGridPlugin, adaptivePlugin, interactionPlugin]}
          initialView="dayGridMonth"
          fixedWeekCount={false}
          timeZone='local'
          customButtons={{
            printButton: {
              text: 'Print',
              click: -> printCalendar() unless loading
            }
            resetButton: {
              text: 'Reset',
              click: -> resetFilters() unless loading
            }
          }}
          headerToolbar={{
            left: 'title',
            right: 'resetButton printButton today prev,next'
          }}
          datesSet={handleDatesSet}
          dateClick={(info) -> setSelectedDate(new Date(info.dateStr)) unless loading}
          eventClick={handleCalendarEventClick}
          events={calendarEvents}
          height="auto"
        />
      </div>
    </div>

    {selectedPlaces.length > 0 &&
      <section id="available-amenity-reservations">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <h1 className="section-heading-lowercase my-3">Available Amenity Reservations</h1>
          </div>
        </div>

        <div className="row">
          {paginatedSelectedPlaces.map((place, index) ->
            <div key={place.id or index} className="col-sm-6 col-lg-4 col-xl-3">
              <ParkCard
                park={place}
                base="/community-calendar"
                selectedDate={if selectedDate instanceof Date then format(selectedDate, "yyyy-MM-dd") else format(new Date(), "yyyy-MM-dd")}
              />
            </div>
          )}
        </div>
        {selectedPlacesTotalPages > 1 &&
          <div className="row justify-content-center mt-4 calendar-reservation-pagination">
            <div className="col-12">
              <nav aria-label="Open to Reserve pagination">
                <ul className="pagination">
                  <li className={classNames('page-item', {disabled: selectedPlacesCurrentPage == 1})}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={-> setSelectedPlacesPage(selectedPlacesCurrentPage - 1)}
                      disabled={selectedPlacesCurrentPage == 1}
                    >
                      Previous
                    </button>
                  </li>
                  {[...Array(selectedPlacesTotalPages)].map((_, index) ->
                    <li className={classNames('page-item', {active: selectedPlacesCurrentPage == index + 1})} key={index}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={-> setSelectedPlacesPage(index + 1)}
                      >
                        {index + 1}
                      </button>
                    </li>
                  )}
                  <li className={classNames('page-item', {disabled: selectedPlacesCurrentPage == selectedPlacesTotalPages})}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={-> setSelectedPlacesPage(selectedPlacesCurrentPage + 1)}
                      disabled={selectedPlacesCurrentPage == selectedPlacesTotalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        }
      </section>
    }
  </TopLevelPage>

export default withErrorBoundary connected CalendarView
